/* Hand a finished jsPDF document to the visitor — either save it as a file or
   open the browser's print dialog on exactly the same document.

   Printing goes through a hidden, same-origin iframe holding the PDF blob, so
   the dialog opens over the dashboard: no popup window for a blocker to eat,
   and nothing left behind in the Downloads folder. iOS/Safari won't let script
   drive an iframe's print(), so there the blob is opened in a new tab instead —
   the built-in PDF viewer offers Print from its own toolbar. */

// iOS (any browser — they all use WebKit) and desktop Safari. Chrome/Firefox on
// iOS identify as CriOS/FxiOS but are still WebKit, so the platform test comes
// first. iPadOS 13+ reports itself as "MacIntel", hence the touch-points check.
const isWebkitPrintBlocked = () => {
  const ua = navigator.userAgent || ''
  const iOS = /iPad|iPhone|iPod/.test(ua)
    || (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1)
  const safari = /safari/i.test(ua) && !/chrome|chromium|crios|android|edg|fxios/i.test(ua)
  return iOS || safari
}

// The iframe must outlive the print dialog — tearing it down early cancels the
// job — so cleanup waits for `afterprint`, with a long timer as the backstop
// for browsers that never fire it.
const cleanupAfterPrint = (frame, blobUrl) => {
  let done = false
  const remove = () => {
    if (done) return
    done = true
    window.removeEventListener('afterprint', remove)
    if (frame.parentNode) frame.parentNode.removeChild(frame)
    URL.revokeObjectURL(blobUrl)
  }
  window.addEventListener('afterprint', remove)
  setTimeout(remove, 60000)
}

export const printPdf = (pdf) => {
  const blobUrl = pdf.output('bloburl').toString()
  if (isWebkitPrintBlocked()) {
    window.open(blobUrl, '_blank')
    return
  }
  const frame = document.createElement('iframe')
  frame.setAttribute('aria-hidden', 'true')
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;'
  frame.onload = () => {
    try {
      frame.contentWindow.focus()
      frame.contentWindow.print()
      cleanupAfterPrint(frame, blobUrl)
    } catch {
      // Cross-origin or blocked — fall back to the viewer tab.
      if (frame.parentNode) frame.parentNode.removeChild(frame)
      window.open(blobUrl, '_blank')
    }
  }
  frame.src = blobUrl
  document.body.appendChild(frame)
}

// `mode` is 'download' (default) or 'print'.
export const deliverPdf = (pdf, mode, filename) => {
  if (mode === 'print') printPdf(pdf)
  else pdf.save(filename)
}

export default deliverPdf
